import React from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, FontWeight, SkeletonLoader, Text } from '@clutter/clean';

import { RateGroupKindEnum } from '@graphql/platform';

import { COMMITMENT_TO_FREE_SUBSEQUENT_JOBS } from '@root/constants/pricing';
import { Commitment } from '@root/resources/types/commitment';

import { Currency } from '@shared/currency';
import { Fallback } from '@shared/fallback';

import { PricingSummary } from '@utils/hooks/pricing/use_pricing_summary';

import { RadioCard } from '../../../subcomponents/radio_card';

const StyledRadioCard = styled(RadioCard)`
  & > div {
    height: 100%;
  }
`;

const MonthlyPrice = ({ amount }: { amount?: number }) => {
  if (!amount) {
    return null;
  }
  return (
    <>
      <Currency amount={amount} />
      /mo
    </>
  );
};

const CardLayout: React.FC<{
  title: React.ReactNode;
  description: React.ReactNode;
  price?: React.ReactNode;
  subtext?: React.ReactNode;
  loading?: boolean;
}> = ({ title, description, price, subtext, loading }) => {
  return (
    <Box.Flex
      justifyContent="space-between"
      gap={['24px', null, '16px']}
      height="100%"
      color={COLORS.tealDark}
    >
      <Box.Flex
        flexDirection="column"
        height="100%"
        padding="0 4px"
        flexGrow={1}
      >
        <Box.FlexItem margin="0 0 8px">
          <Text.Title size="extraSmall">{title}</Text.Title>
        </Box.FlexItem>
        <Box.FlexItem>
          <Text.Callout>{description}</Text.Callout>
        </Box.FlexItem>
      </Box.Flex>
      {price && (
        <Fallback
          delayMs={0}
          placeholder={<Box.Flex height="32px" width="80px" flexShrink={0} />}
          loader={<SkeletonLoader height="32px" width="80px" flexShrink={0} />}
          value={!loading}
        >
          <Box.Flex
            gap="8px"
            flexDirection="column"
            alignItems="flex-end"
            flexShrink={0}
          >
            <Box.FlexItem>
              <Box
                padding="2px 4px"
                background={COLORS.dust}
                borderRadius="4px"
              >
                <Text.Body weight={FontWeight.Medium}>{price}</Text.Body>
              </Box>
            </Box.FlexItem>
            {subtext && (
              <Box.FlexItem textAlign="right">
                <Text.Callout color={COLORS.hippo} weight={FontWeight.Medium}>
                  {subtext}
                </Text.Callout>
              </Box.FlexItem>
            )}
          </Box.Flex>
        </Fallback>
      )}
    </Box.Flex>
  );
};

const CardContent = ({
  titleText,
  description,
  monthlyAmount,
  pricingSummary,
  flatFee,
  pricingSummaryLoading,
}: {
  titleText: React.ReactNode;
  description: React.ReactNode;
  monthlyAmount?: number;
  pricingSummary?: PricingSummary;
  flatFee?: number;
  pricingSummaryLoading?: boolean;
}) => {
  const moverMonthlyAmount = pricingSummary?.moverMonthlyAmount;

  return (
    <CardLayout
      title={titleText}
      description={description}
      price={<MonthlyPrice amount={monthlyAmount} />}
      loading={pricingSummaryLoading}
      subtext={
        <>
          {!moverMonthlyAmount || !monthlyAmount ? (
            <></>
          ) : moverMonthlyAmount > monthlyAmount ? (
            <span style={{ textDecoration: 'line-through' }}>
              <MonthlyPrice amount={moverMonthlyAmount} />
            </span>
          ) : null}
          {!!flatFee && (
            <div>
              +<Currency amount={flatFee} /> fee
            </div>
          )}
        </>
      }
    />
  );
};

export const TieredCards: React.FC<{
  commitment?: Commitment;
  pricingSummary?: PricingSummary;
  pricingSummaryLoading?: boolean;
  onChange: (commitment: Commitment) => void;
  scrollToStep: () => void;
}> = ({ commitment, pricingSummary, pricingSummaryLoading, onChange }) => {
  const moverCard = (
    <StyledRadioCard
      selected={commitment === RateGroupKindEnum.Mover}
      onChange={() => onChange(RateGroupKindEnum.Mover)}
      name="commitment"
      value="mover"
    >
      <CardContent
        titleText="Month to month"
        description={
          <>Free initial pickup and delivery. Purchase more anytime.</>
        }
        monthlyAmount={pricingSummary?.moverMonthlyAmount}
        pricingSummary={pricingSummary}
        flatFee={pricingSummary?.moverOnboardingFeeAmount}
        pricingSummaryLoading={pricingSummaryLoading}
      />
    </StyledRadioCard>
  );

  const flexerCard = (
    <StyledRadioCard
      selected={commitment === RateGroupKindEnum.Flexer}
      onChange={() => onChange(RateGroupKindEnum.Flexer)}
      name="commitment"
      value="flexer"
    >
      <CardContent
        titleText={
          <>
            4 <span style={{ whiteSpace: 'nowrap' }}>month minimum</span>
          </>
        }
        description={
          <>
            Free initial pickup and delivery.{' '}
            <b>
              {COMMITMENT_TO_FREE_SUBSEQUENT_JOBS[RateGroupKindEnum.Flexer]}{' '}
              additional pickup or delivery
            </b>{' '}
            free of charge.
          </>
        }
        monthlyAmount={pricingSummary?.flexerMonthlyAmount}
        pricingSummary={pricingSummary}
        flatFee={pricingSummary?.flexerOnboardingFeeAmount}
        pricingSummaryLoading={pricingSummaryLoading}
      />
    </StyledRadioCard>
  );

  const saverCard = (
    <StyledRadioCard
      selected={commitment === RateGroupKindEnum.Saver}
      onChange={() => onChange(RateGroupKindEnum.Saver)}
      name="commitment"
      value="saver"
    >
      <CardContent
        titleText={
          <>
            8 <span style={{ whiteSpace: 'nowrap' }}>month minimum</span>
          </>
        }
        description={
          <>
            Free initial pickup and delivery.{' '}
            <b>
              {COMMITMENT_TO_FREE_SUBSEQUENT_JOBS[RateGroupKindEnum.Saver]}{' '}
              additional pickups or deliveries
            </b>{' '}
            free of charge.
          </>
        }
        monthlyAmount={pricingSummary?.saverMonthlyAmount}
        pricingSummary={pricingSummary}
        flatFee={pricingSummary?.saverOnboardingFeeAmount}
        pricingSummaryLoading={pricingSummaryLoading}
      />
    </StyledRadioCard>
  );
  return (
    <Box.Grid gridTemplateColumns="1fr" gridGap={['8px', null, '12px']}>
      {moverCard}
      {flexerCard}
      {saverCard}
    </Box.Grid>
  );
};
